// Each answer as one thing (TOM-709, the Neo answers plan): the owner's
// question, Neo's answer under it with its conclusion first, a very brief note
// of where the numbers came from, and up to three follow-ups under the newest
// answer only.
//
// The chat keeps a flat list of entries. An answer is the owner's question and
// every Neo entry after it up to the next question: the answer's own bubble and
// its records (receipts, alarm cards, the "✓" chips), keyed `${answerId}-…`,
// whether they arrived before the bubble or after it, and any note Neomaris
// posted meanwhile. Entries before the first question (the greeting, an alarm
// message) form a group with no question.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

import {
  neoCardTitle,
  neoChartTitle,
  splitNeoChartContent,
  stripNeoChartMarkers,
  type NeoAnswerRecord,
  type NeoAnswerSource,
  type NeoAnswerChart,
  type NeoFollowup,
} from '@neomaris/protocol';

import type { NeoAnswerCard } from './neoAnswerCards';

/**
 * The capability a chat sends to have Neo's answers come with their record
 * (contract §2): the full chat page, home's chat and the sparkle's. The board's
 * Edit chat does not send it.
 */
export const NEO_ANSWERS_CAPABILITY = 'answers';

/** What the grouping reads of a chat entry. */
export interface NeoAnswerEntry {
  id: string;
  role: 'user' | 'assistant';
  /** A line the chat or the server wrote: the greeting, a chip, a record, a note. */
  local?: boolean;
  /** Neo's greeting on the boat's home (TOM-920): an answer that opens a group of its own. */
  greeting?: boolean;
  /**
   * Set on Neo's question after Ask Neo on a panel (TOM-958): a group of its
   * own, so the answer before it keeps no chips under it.
   */
  panelQuestion?: unknown;
}

/** One question and Neo's entries under it. */
export interface NeoAnswerGroup<M extends NeoAnswerEntry> {
  /** The owner's question; null for what came before the first one. */
  question: M | null;
  /**
   * Neo's answer: the group's first entry that is not a local line. Null while
   * none has arrived, and in a group of local lines only.
   */
  answer: M | null;
  /** Every entry after the question, in order, the answer included. */
  entries: M[];
}

/** The chat's entries as answers, in order. */
export function groupNeoAnswers<M extends NeoAnswerEntry>(
  messages: readonly M[]
): NeoAnswerGroup<M>[] {
  const groups: NeoAnswerGroup<M>[] = [];
  let current: NeoAnswerGroup<M> | null = null;
  for (const message of messages) {
    if (message.role === 'user') {
      current = { question: message, answer: null, entries: [] };
      groups.push(current);
      continue;
    }
    // Neo's greeting answers no question: its own group, so its chips are the
    // newest when it is the newest answer (TOM-920). Neo's question about a
    // panel opens one too, with no answer: no chips show above it (TOM-958).
    if (current === null || message.greeting === true || message.panelQuestion !== undefined) {
      current = { question: null, answer: null, entries: [] };
      groups.push(current);
    }
    current.entries.push(message);
    if (current.answer === null && message.local !== true) current.answer = message;
  }
  return groups;
}

/** What the chips read of an answer's entry. */
export interface NeoChipEntry extends NeoAnswerEntry {
  /** The stored answer's id: the follow-up names it (contract §2). */
  messageId?: string;
  stopped?: boolean;
  answer?: NeoAnswerRecord;
}

/** The follow-ups to show, under which answer, naming which stored message. */
export interface NeoAnswerChips {
  /** The answer entry's id in the chat. */
  answerId: string;
  /** The stored assistant message the follow-up reference names. */
  messageId: string;
  followups: readonly NeoFollowup[];
}

/**
 * The newest answer's follow-ups, or null. Only the newest answer has chips,
 * and only once it is finished and stored: not while it streams or a new
 * question is on its way, not when the owner stopped it, and not under an
 * older answer once a newer question was asked.
 */
export function neoAnswerChips<M extends NeoChipEntry>(
  groups: readonly NeoAnswerGroup<M>[],
  state: { streamingId: string | null; sending: boolean }
): NeoAnswerChips | null {
  if (state.sending) return null;
  const last = groups.at(-1);
  const answer = last?.answer ?? null;
  if (answer === null || answer.id === state.streamingId) return null;
  if (answer.stopped === true || answer.messageId === undefined) return null;
  const followups = answer.answer?.followups ?? [];
  if (followups.length === 0) return null;
  return { answerId: answer.id, messageId: answer.messageId, followups };
}

// ----- The chat's title -----------------------------------------------------------

const TITLE_MAX = 60;

/**
 * The conversation's name in the chat's header: "Main chat" for the main chat,
 * else the owner's first question, as the Chats page names a chat before it is
 * renamed, cut short; "New chat" before anything is asked.
 */
export function neoChatTitle(
  messages: readonly { role: 'user' | 'assistant'; content: string }[],
  options: { main: boolean }
): string {
  if (options.main) return 'Main chat';
  const first = messages
    .find((m) => m.role === 'user')
    ?.content.replace(/\s+/g, ' ')
    .trim();
  if (first === undefined || first.length === 0) return 'New chat';
  const chars = [...first];
  return chars.length <= TITLE_MAX
    ? first
    : `${chars
        .slice(0, TITLE_MAX - 1)
        .join('')
        .trimEnd()}…`;
}

// ----- The conclusion -----------------------------------------------------------

/** An answer split at its conclusion (contract §4). */
export interface NeoConclusion {
  /** The leading `## ` line's text, or null when the answer opens otherwise. */
  conclusion: string | null;
  /** The rest of the answer, drawn as its body. */
  body: string;
}

const LEADING_H2 = /^##[ \t]+(?!#)([^\n]*)(?:\n|$)/;

/**
 * The leading H2 of an assistant answer is its conclusion; any other H2 is an
 * ordinary heading, and an answer that opens with anything else has none.
 * While the answer streams, its first characters may be a heading still on its
 * way ("#", "##"): nothing is shown for them until the line says what it is.
 */
export function neoConclusion(content: string, streaming = false): NeoConclusion {
  const text = content.replace(/^\s+/, '');
  if (streaming && /^#{1,2}[ \t]*$/.test(text)) return { conclusion: null, body: '' };
  const match = LEADING_H2.exec(text);
  if (match === null) return { conclusion: null, body: content };
  const line = stripNeoChartMarkers(match[1]!)
    .replace(/[ \t]+#+[ \t]*$/, '')
    .trim();
  if (line.length === 0) return { conclusion: null, body: content };
  return { conclusion: line, body: text.slice(match[0].length).replace(/^\s+/, '') };
}

// ----- The source line ------------------------------------------------------------

const DAY_MS = 86_400_000;

interface ZonedParts {
  day: number;
  weekday: string;
  month: string;
  date: number;
  time: string;
}

function zonedParts(ms: number, timeZone: string | undefined): ZonedParts | null {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    // A zone this browser doesn't know: the viewer's own clock.
    format = new Intl.DateTimeFormat('en-US', options);
  }
  const parts: Record<string, string> = {};
  for (const part of format.formatToParts(ms)) parts[part.type] = part.value;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const date = Number(parts.day);
  if (![year, month, date].every(Number.isFinite)) return null;
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    Date.UTC(2000, month - 1, 1)
  );
  return {
    // The calendar day as a count, so two days in the zone compare by subtraction.
    day: Math.round(Date.UTC(year, month - 1, date) / DAY_MS),
    weekday: parts.weekday ?? '',
    month: monthName,
    date,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/**
 * The answer's very brief source line (Mike, 17 Oct): its source and its time,
 * nothing more — "Boat readings · 14:20" today, "Boat readings · Mon 14:20"
 * within the last week, "Boat readings · Oct 3 14:20" before that, in the zone
 * the answer gives (else the viewer's own). A source without a time is its
 * label alone ("Estimate"). Null when the answer has none.
 */
export function neoSourceLine(
  source: NeoAnswerSource | null | undefined,
  nowMs: number,
  viewerZone?: string
): string | null {
  if (source === null || source === undefined) return null;
  const label = source.label.trim();
  if (label.length === 0) return null;
  const when = neoShortTime(source.at, nowMs, source.timeZone ?? viewerZone);
  return when === null ? label : `${label} · ${when}`;
}

/**
 * A time said short, in `zone` (else the viewer's): "14:20" today, "Fri 14:20"
 * within the last week, "Oct 3 14:20" before that. Null for a time it cannot
 * read.
 */
export function neoShortTime(
  iso: string | undefined,
  nowMs: number,
  zone: string | undefined
): string | null {
  const at = iso === undefined ? NaN : Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const then = zonedParts(at, zone);
  const now = zonedParts(nowMs, zone);
  if (then === null || now === null) return null;
  const daysAgo = now.day - then.day;
  if (daysAgo === 0) return then.time;
  if (daysAgo > 0 && daysAgo < 7) return `${then.weekday} ${then.time}`;
  return `${then.month} ${then.date} ${then.time}`;
}

/**
 * A range said short, in `zone`: "Oct 3 10:50–13:40" within one day,
 * "Oct 3 10:50 – Oct 4 09:10" across days. Null when either end cannot be read.
 */
export function neoShortRange(
  startIso: string,
  endIso: string,
  nowMs: number,
  zone: string | undefined
): string | null {
  const start = neoShortTime(startIso, nowMs, zone);
  const end = neoShortTime(endIso, nowMs, zone);
  if (start === null || end === null) return null;
  const a = zonedParts(Date.parse(startIso), zone);
  const b = zonedParts(Date.parse(endIso), zone);
  if (a === null || b === null) return null;
  if (a.day === b.day) return `${start}–${b.time}`;
  // Across days both ends say their date, so an end today never reads as a
  // time on the start's day ("Jul 19 15:12 – Oct 17 15:12").
  return `${a.month} ${a.date} ${a.time} – ${b.month} ${b.date} ${b.time}`;
}

// ----- Copy ---------------------------------------------------------------------

/** Markdown's own marks taken out of a stretch of text, for pasting as plain text. */
function plainText(markdown: string): string {
  return markdown
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/__([^_\n]+)__/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1 ($2)');
}

/**
 * What Copy puts on the clipboard: the answer's text as plain text, each chart
 * or card at its marker as its title, and a marker naming nothing dropped.
 */
export function neoAnswerCopyText(
  content: string,
  charts: readonly NeoAnswerChart[] = [],
  cards: readonly NeoAnswerCard[] = []
): string {
  const pieces: string[] = [];
  for (const segment of splitNeoChartContent(content)) {
    if (segment.kind === 'text') {
      pieces.push(plainText(segment.text).trim());
      continue;
    }
    if (segment.kind === 'card') {
      const card = cards.find((c) => c.id === segment.id);
      if (card !== undefined) pieces.push(neoCardTitle(card));
      continue;
    }
    const chart = charts.find((c) => c.id === segment.id);
    if (chart !== undefined) pieces.push(neoChartTitle(chart));
  }
  return pieces
    .filter((piece) => piece.length > 0)
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ----- Follow-ups ---------------------------------------------------------------

/**
 * How a chip acts: `send` asks Neo with the chip's label as the owner's
 * message (ask, recalculate and drill down, which the server applies);
 * `view` switches a chart's view (W3 fills it); `evidence` opens Behind the
 * answer (W2), shown only where that exists.
 */
export type NeoChipAction = 'send' | 'view' | 'evidence';

export function neoChipAction(followup: NeoFollowup): NeoChipAction {
  switch (followup.intent.kind) {
    case 'show_view':
      return 'view';
    case 'inspect_evidence':
      return 'evidence';
    default:
      return 'send';
  }
}

/**
 * The chips to draw, each with its index in the answer's follow-ups (the
 * reference the server checks). An `inspect_evidence` chip opens Behind the
 * answer where it exists, and elsewhere asks Neo like a question (contract,
 * 17 Oct). A `show_view` chip is drawn only where the chat can switch a
 * chart's view, so no chip does nothing.
 */
export function neoVisibleChips(
  followups: readonly NeoFollowup[],
  options: { evidence: boolean; view: boolean }
): { followup: NeoFollowup; index: number; action: NeoChipAction }[] {
  return followups
    .map((followup, index) => {
      const action = neoChipAction(followup);
      return {
        followup,
        index,
        action: action === 'evidence' && !options.evidence ? ('send' as const) : action,
      };
    })
    .filter((chip) => chip.action !== 'view' || options.view);
}
